import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { LoadingContext } from "../LoadingContext";
import Loading from "./Loading";
import "./AdminSalesAnalytics.css";
import AdminProductSalesReport from "./AdminProductSalesReport";
import Swal from "sweetalert2";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { toLocalDateString } from "../dateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MySwal = Swal.mixin({
    customClass: {
        container: "my-swal-container",
    },
});

function AdminSalesAnalytics() {
    const { authFetch } = useContext(AuthContext);
    const { setIsLoading } = useContext(LoadingContext);
    const navigate = useNavigate();

    // DD/MM/YYYY. toLocaleDateString() renders US format (7/29/2026).
    const formatDateOnly = (value) => {
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date)) return "N/A";
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        return `${day}/${month}/${date.getFullYear()}`;
    };

    // DD/MM/YYYY HH:mm. toLocaleString() also embeds a comma, which splits the value
    // across two cells when the CSV is opened in Excel.
    const formatDateTime = (value) => {
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date)) return "N/A";
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${formatDateOnly(date)} ${hours}:${minutes}`;
    };
    const [loading, setLoading] = useState(true);

    const [salesData, setSalesData] = useState({
        totalRevenue: 0,
        orderCount: 0,
        revenueByDay: [],
        ordersByDay: []
    });
    const [trendsData, setTrendsData] = useState({
        revenueByPeriod: [],
        ordersByPeriod: []
    });
    const [salesListCustomDateRange, setSalesListCustomDateRange] = useState({
        startDate: null,
        endDate: null,
    });

    const [dateRange, setDateRange] = useState("last30days");
    const [trendsDateRange, setTrendsDateRange] = useState("daily");
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [customDateRange, setCustomDateRange] = useState({
        startDate: null,
        endDate: null,
    });

    const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
    const toggleExportDropdown = () => {
        setIsExportDropdownOpen(!isExportDropdownOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdowns = document.getElementsByClassName('export-dropdown-container');
            for (let i = 0; i < dropdowns.length; i++) {
                if (isExportDropdownOpen && !dropdowns[i].contains(event.target)) {
                    setIsExportDropdownOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isExportDropdownOpen]);

    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedYear, setSelectedYear] = useState("");
    const [allOrders, setAllOrders] = useState([]);
    const [salesListView, setSalesListView] = useState("last30days");
    const [salesListPage, setSalesListPage] = useState(1);
    const [salesListPerPage] = useState(12);
    const [filteredSalesList, setFilteredSalesList] = useState([]);
    const [salesListSummary, setSalesListSummary] = useState({
        totalOrders: 0,
        totalAmount: 0
    });

    const formatCurrency = (amount) => {
        return `RM${parseFloat(amount).toFixed(2)}`;
    };

    useEffect(() => {
        if (salesListView === "customRange" &&
            salesListCustomDateRange.startDate &&
            salesListCustomDateRange.endDate) {
            updateSalesList();
        }
    }, [salesListCustomDateRange, salesListView]);

    const getDateRangeParams = () => {
        const today = new Date();
        let startDate, endDate;

        switch (dateRange) {
            case "today":
                startDate = today;
                endDate = today;
                break;
            case "yesterday":
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 1);
                endDate = startDate;
                break;
            case "last7days":
                endDate = today;
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 7);
                break;
            case "last30days":
                endDate = today;
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 30);
                break;
            case "monthToDate":
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = today;
                break;
            case "quarterToDate":
                const currentMonth = today.getMonth();
                let quarterStartMonth;
                if (currentMonth >= 0 && currentMonth <= 2) {
                    quarterStartMonth = 0;
                } else if (currentMonth >= 3 && currentMonth <= 5) {
                    quarterStartMonth = 3;
                } else if (currentMonth >= 6 && currentMonth <= 8) {
                    quarterStartMonth = 6;
                } else {
                    quarterStartMonth = 9;
                }
                startDate = new Date(today.getFullYear(), quarterStartMonth, 1);
                endDate = today;
                break;
            case "yearToDate":
                startDate = new Date(today.getFullYear(), 0, 1);
                endDate = today;
                break;
            case "1year":
                endDate = today;
                startDate = new Date(today);
                startDate.setFullYear(today.getFullYear() - 1);
                break;
            case "custom":
                startDate = customDateRange.startDate ? new Date(customDateRange.startDate) : null;
                endDate = customDateRange.endDate ? new Date(customDateRange.endDate) : null;
                break;
            default:
                endDate = today;
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 30);
        }

        return {
            startDate: startDate ? toLocalDateString(startDate) : null,
            endDate: endDate ? toLocalDateString(endDate) : null,
        };
    };

    const getReportData = useCallback(() => {
        if (!allOrders || allOrders.length === 0) return { data: [], summary: { totalOrders: 0, totalAmount: 0 } };

        const today = new Date();
        let filteredOrders = [];
        let startDate;
        let aggregatedData = [];

        switch (salesListView) {
            case "last30days":
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });

                for (let i = 0; i < 30; i++) {
                    const day = new Date(today);
                    day.setDate(today.getDate() - i);
                    day.setHours(0, 0, 0, 0);

                    const nextDay = new Date(day);
                    nextDay.setDate(day.getDate() + 1);

                    const dayOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= day && orderDate < nextDay;
                    });

                    if (dayOrders.length > 0) {
                        const totalAmount = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        aggregatedData.push({
                            period: toLocalDateString(day),
                            displayDate: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
                            orderCount: dayOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
                break;

            case "monthlyPast12":
                startDate = new Date(today);
                startDate.setMonth(today.getMonth() - 11);
                startDate.setDate(1);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });

                for (let i = 0; i < 12; i++) {
                    const month = new Date(today);
                    month.setMonth(today.getMonth() - i);
                    month.setDate(1);
                    month.setHours(0, 0, 0, 0);

                    const nextMonth = new Date(month);
                    nextMonth.setMonth(month.getMonth() + 1);

                    const monthOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= month && orderDate < nextMonth;
                    });

                    if (monthOrders.length > 0) {
                        const totalAmount = monthOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        const monthNames = ["January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"];
                        aggregatedData.push({
                            period: `${month.getFullYear()}-${month.getMonth() + 1}`,
                            displayDate: `${monthNames[month.getMonth()]} ${month.getFullYear()}`,
                            orderCount: monthOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
                break;

            case "yearlyPast5":

                startDate = new Date(today);
                startDate.setFullYear(today.getFullYear() - 4);
                startDate.setMonth(0, 1);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });


                for (let i = 0; i < 5; i++) {
                    const year = today.getFullYear() - i;
                    const yearStart = new Date(year, 0, 1);
                    const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);

                    const yearOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= yearStart && orderDate <= yearEnd;
                    });

                    if (yearOrders.length > 0) {
                        const totalAmount = yearOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        aggregatedData.push({
                            period: year.toString(),
                            displayDate: year.toString(),
                            orderCount: yearOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
                break;

            case "yearToDate":

                startDate = new Date(today.getFullYear(), 0, 1);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });


                for (let i = 0; i <= today.getMonth(); i++) {
                    const month = new Date(today.getFullYear(), i, 1);
                    const nextMonth = new Date(today.getFullYear(), i + 1, 1);

                    const monthOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= month && orderDate < nextMonth;
                    });

                    if (monthOrders.length > 0) {
                        const totalAmount = monthOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        const monthNames = ["January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"];
                        aggregatedData.push({
                            period: `${today.getFullYear()}-${i + 1}`,
                            displayDate: `${monthNames[i]} ${today.getFullYear()}`,
                            orderCount: monthOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
                break;

            case "customRange":
                if (salesListCustomDateRange.startDate && salesListCustomDateRange.endDate) {
                    const startDate = new Date(salesListCustomDateRange.startDate);
                    startDate.setHours(0, 0, 0, 0);

                    const endDate = new Date(salesListCustomDateRange.endDate);
                    endDate.setHours(23, 59, 59, 999);

                    filteredOrders = allOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= startDate && orderDate <= endDate;
                    });

                    const diffTime = Math.abs(endDate - startDate);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    // Generate data for each day in the range
                    for (let i = 0; i <= diffDays; i++) {
                        const day = new Date(startDate);
                        day.setDate(startDate.getDate() + i);
                        day.setHours(0, 0, 0, 0);

                        const nextDay = new Date(day);
                        nextDay.setDate(day.getDate() + 1);

                        const dayOrders = filteredOrders.filter(order => {
                            const orderDate = new Date(order.orderDate);
                            return orderDate >= day && orderDate < nextDay;
                        });

                        if (dayOrders.length > 0) {
                            const totalAmount = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                            aggregatedData.push({
                                period: toLocalDateString(day),
                                displayDate: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
                                orderCount: dayOrders.length,
                                totalAmount: totalAmount
                            });
                        }
                    }
                }
                break;

            case "specificMonth":
                if (selectedMonth) {
                    const monthParts = selectedMonth.split('-');
                    const monthIndex = parseInt(monthParts[0]);
                    const monthYear = parseInt(monthParts[1]);

                    const monthStartDate = new Date(monthYear, monthIndex, 1);
                    const monthEndDate = new Date(monthYear, monthIndex + 1, 0, 23, 59, 59, 999);

                    filteredOrders = allOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= monthStartDate && orderDate <= monthEndDate;
                    });


                    const daysInMonth = monthEndDate.getDate();
                    for (let i = 1; i <= daysInMonth; i++) {
                        const day = new Date(monthYear, monthIndex, i);
                        const nextDay = new Date(monthYear, monthIndex, i + 1);

                        const dayOrders = filteredOrders.filter(order => {
                            const orderDate = new Date(order.orderDate);
                            return orderDate >= day && orderDate < nextDay;
                        });

                        if (dayOrders.length > 0) {
                            const totalAmount = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                            aggregatedData.push({
                                period: toLocalDateString(day),
                                displayDate: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
                                orderCount: dayOrders.length,
                                totalAmount: totalAmount
                            });
                        }
                    }
                }
                break;

            case "specificYear":
                if (selectedYear) {
                    const yearValue = parseInt(selectedYear);
                    const yearStart = new Date(yearValue, 0, 1);
                    const yearEnd = new Date(yearValue, 11, 31, 23, 59, 59, 999);

                    filteredOrders = allOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= yearStart && orderDate <= yearEnd;
                    });


                    const monthNames = ["January", "February", "March", "April", "May", "June",
                        "July", "August", "September", "October", "November", "December"];
                    for (let i = 0; i < 12; i++) {
                        const month = new Date(yearValue, i, 1);
                        const nextMonth = new Date(yearValue, i + 1, 1);

                        const monthOrders = filteredOrders.filter(order => {
                            const orderDate = new Date(order.orderDate);
                            return orderDate >= month && orderDate < nextMonth;
                        });

                        if (monthOrders.length > 0) {
                            const totalAmount = monthOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                            aggregatedData.push({
                                period: `${yearValue}-${i + 1}`,
                                displayDate: monthNames[i],
                                orderCount: monthOrders.length,
                                totalAmount: totalAmount
                            });
                        }
                    }
                }
                break;

            default:

                startDate = new Date(today);
                startDate.setDate(today.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });


                for (let i = 0; i < 30; i++) {
                    const day = new Date(today);
                    day.setDate(today.getDate() - i);
                    day.setHours(0, 0, 0, 0);

                    const nextDay = new Date(day);
                    nextDay.setDate(day.getDate() + 1);

                    const dayOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= day && orderDate < nextDay;
                    });

                    if (dayOrders.length > 0) {
                        const totalAmount = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        aggregatedData.push({
                            period: toLocalDateString(day),
                            displayDate: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
                            orderCount: dayOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
        }


        const sortedData = aggregatedData.sort((a, b) => b.period.localeCompare(a.period));


        const totalOrders = filteredOrders.length;
        const totalAmount = filteredOrders.reduce((sum, order) => sum + order.finalAmount, 0);


        return {
            data: sortedData,
            summary: {
                totalOrders,
                totalAmount
            }
        };
    }, [allOrders, salesListView, selectedMonth, selectedYear, salesListCustomDateRange]);


    const updateSalesList = useCallback(() => {
        if (!allOrders || allOrders.length === 0) return;

        const today = new Date();
        let filteredOrders = [];
        let startDate;
        let aggregatedData = [];

        switch (salesListView) {
            case "last30days":

                startDate = new Date(today);
                startDate.setDate(today.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });


                for (let i = 0; i < 30; i++) {
                    const day = new Date(today);
                    day.setDate(today.getDate() - i);
                    day.setHours(0, 0, 0, 0);

                    const nextDay = new Date(day);
                    nextDay.setDate(day.getDate() + 1);

                    const dayOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= day && orderDate < nextDay;
                    });

                    if (dayOrders.length > 0) {
                        const totalAmount = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        aggregatedData.push({
                            period: toLocalDateString(day),
                            displayDate: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
                            orderCount: dayOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
                break;

            case "monthlyPast12":

                startDate = new Date(today);
                startDate.setMonth(today.getMonth() - 11);
                startDate.setDate(1);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });


                for (let i = 0; i < 12; i++) {
                    const month = new Date(today);
                    month.setMonth(today.getMonth() - i);
                    month.setDate(1);
                    month.setHours(0, 0, 0, 0);

                    const nextMonth = new Date(month);
                    nextMonth.setMonth(month.getMonth() + 1);

                    const monthOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= month && orderDate < nextMonth;
                    });

                    if (monthOrders.length > 0) {
                        const totalAmount = monthOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        const monthNames = ["January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"];
                        aggregatedData.push({
                            period: `${month.getFullYear()}-${month.getMonth() + 1}`,
                            displayDate: `${monthNames[month.getMonth()]} ${month.getFullYear()}`,
                            orderCount: monthOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
                break;

            case "yearlyPast5":

                startDate = new Date(today);
                startDate.setFullYear(today.getFullYear() - 4);
                startDate.setMonth(0, 1);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });


                for (let i = 0; i < 5; i++) {
                    const year = today.getFullYear() - i;
                    const yearStart = new Date(year, 0, 1);
                    const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);

                    const yearOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= yearStart && orderDate <= yearEnd;
                    });

                    if (yearOrders.length > 0) {
                        const totalAmount = yearOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        aggregatedData.push({
                            period: year.toString(),
                            displayDate: year.toString(),
                            orderCount: yearOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
                break;

            case "yearToDate":

                startDate = new Date(today.getFullYear(), 0, 1);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });


                for (let i = 0; i <= today.getMonth(); i++) {
                    const month = new Date(today.getFullYear(), i, 1);
                    const nextMonth = new Date(today.getFullYear(), i + 1, 1);

                    const monthOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= month && orderDate < nextMonth;
                    });

                    if (monthOrders.length > 0) {
                        const totalAmount = monthOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        const monthNames = ["January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December"];
                        aggregatedData.push({
                            period: `${today.getFullYear()}-${i + 1}`,
                            displayDate: `${monthNames[i]} ${today.getFullYear()}`,
                            orderCount: monthOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
                break;

            case "customRange":
                if (salesListCustomDateRange.startDate && salesListCustomDateRange.endDate) {
                    const startDate = new Date(salesListCustomDateRange.startDate);
                    startDate.setHours(0, 0, 0, 0);

                    const endDate = new Date(salesListCustomDateRange.endDate);
                    endDate.setHours(23, 59, 59, 999);

                    filteredOrders = allOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= startDate && orderDate <= endDate;
                    });
                    const diffTime = Math.abs(endDate - startDate);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    for (let i = 0; i <= diffDays; i++) {
                        const day = new Date(startDate);
                        day.setDate(startDate.getDate() + i);
                        day.setHours(0, 0, 0, 0);

                        const nextDay = new Date(day);
                        nextDay.setDate(day.getDate() + 1);

                        const dayOrders = filteredOrders.filter(order => {
                            const orderDate = new Date(order.orderDate);
                            return orderDate >= day && orderDate < nextDay;
                        });

                        if (dayOrders.length > 0) {
                            const totalAmount = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                            aggregatedData.push({
                                period: toLocalDateString(day),
                                displayDate: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
                                orderCount: dayOrders.length,
                                totalAmount: totalAmount
                            });
                        }
                    }
                }
                break;

            case "specificMonth":
                if (selectedMonth) {
                    const monthParts = selectedMonth.split('-');
                    const monthIndex = parseInt(monthParts[0]);
                    const monthYear = parseInt(monthParts[1]);

                    const monthStartDate = new Date(monthYear, monthIndex, 1);
                    const monthEndDate = new Date(monthYear, monthIndex + 1, 0, 23, 59, 59, 999);

                    filteredOrders = allOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= monthStartDate && orderDate <= monthEndDate;
                    });


                    const daysInMonth = monthEndDate.getDate();
                    for (let i = 1; i <= daysInMonth; i++) {
                        const day = new Date(monthYear, monthIndex, i);
                        const nextDay = new Date(monthYear, monthIndex, i + 1);

                        const dayOrders = filteredOrders.filter(order => {
                            const orderDate = new Date(order.orderDate);
                            return orderDate >= day && orderDate < nextDay;
                        });

                        if (dayOrders.length > 0) {
                            const totalAmount = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                            aggregatedData.push({
                                period: toLocalDateString(day),
                                displayDate: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
                                orderCount: dayOrders.length,
                                totalAmount: totalAmount
                            });
                        }
                    }
                }
                break;

            case "specificYear":
                if (selectedYear) {
                    const yearValue = parseInt(selectedYear);
                    const yearStart = new Date(yearValue, 0, 1);
                    const yearEnd = new Date(yearValue, 11, 31, 23, 59, 59, 999);

                    filteredOrders = allOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= yearStart && orderDate <= yearEnd;
                    });


                    const monthNames = ["January", "February", "March", "April", "May", "June",
                        "July", "August", "September", "October", "November", "December"];
                    for (let i = 0; i < 12; i++) {
                        const month = new Date(yearValue, i, 1);
                        const nextMonth = new Date(yearValue, i + 1, 1);

                        const monthOrders = filteredOrders.filter(order => {
                            const orderDate = new Date(order.orderDate);
                            return orderDate >= month && orderDate < nextMonth;
                        });

                        if (monthOrders.length > 0) {
                            const totalAmount = monthOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                            aggregatedData.push({
                                period: `${yearValue}-${i + 1}`,
                                displayDate: monthNames[i],
                                orderCount: monthOrders.length,
                                totalAmount: totalAmount
                            });
                        }
                    }
                }
                break;

            default:

                startDate = new Date(today);
                startDate.setDate(today.getDate() - 30);
                startDate.setHours(0, 0, 0, 0);

                filteredOrders = allOrders.filter(order => {
                    const orderDate = new Date(order.orderDate);
                    return orderDate >= startDate && orderDate <= today;
                });


                for (let i = 0; i < 30; i++) {
                    const day = new Date(today);
                    day.setDate(today.getDate() - i);
                    day.setHours(0, 0, 0, 0);

                    const nextDay = new Date(day);
                    nextDay.setDate(day.getDate() + 1);

                    const dayOrders = filteredOrders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= day && orderDate < nextDay;
                    });

                    if (dayOrders.length > 0) {
                        const totalAmount = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                        aggregatedData.push({
                            period: toLocalDateString(day),
                            displayDate: `${day.getDate()}/${day.getMonth() + 1}/${day.getFullYear()}`,
                            orderCount: dayOrders.length,
                            totalAmount: totalAmount
                        });
                    }
                }
        }
        const sortedData = aggregatedData.sort((a, b) => b.period.localeCompare(a.period));
        const totalOrders = filteredOrders.length;
        const totalAmount = filteredOrders.reduce((sum, order) => sum + order.finalAmount, 0);

        setFilteredSalesList(sortedData);
        setSalesListSummary({
            totalOrders,
            totalAmount
        });
        setSalesListPage(1);
    }, [allOrders, salesListView, selectedMonth, selectedYear, salesListCustomDateRange]);


    useEffect(() => {
        updateSalesList();
    }, [updateSalesList, salesListView, selectedMonth, selectedYear, allOrders]);


    // Statuses that do NOT represent collected revenue (no successful, un-reversed payment).
    // Revenue, order counts, trends and the sales list all exclude these.
    const NON_REVENUE_STATUSES = ["pending payment", "failed", "cancelled", "refunded"];

    const fetchAllOrders = async () => {
        try {
            const response = await authFetch(`${API_BASE_URL}/api/Orders`);
            if (!response.ok) {
                throw new Error(`Error fetching orders: ${response.statusText}`);
            }
            const data = await response.json();
            // Count only successfully-paid orders (exclude pending/failed/cancelled/refunded)
            const paidOrders = Array.isArray(data)
                ? data.filter(o => o.orderStatus && !NON_REVENUE_STATUSES.includes(o.orderStatus.toString().trim().toLowerCase()))
                : [];
            setAllOrders(paidOrders);
            return paidOrders;
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "Failed to load orders data. Please try again.",
                icon: "error",
                confirmButtonColor: "#45a049",
            });
            return [];
        }
    };


    const calculateSalesData = (orders, { startDate, endDate }) => {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);


        const filteredOrders = orders.filter(order => {
            const orderDate = new Date(order.orderDate);
            return orderDate >= start && orderDate <= end;
        });


        const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.finalAmount, 0);
        const orderCount = filteredOrders.length;


        const dateRange = [];
        const currentDate = new Date(start);
        while (currentDate <= end) {
            dateRange.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }


        const revenueByDay = dateRange.map(date => {
            const dateStr = toLocalDateString(date);
            const dayOrders = filteredOrders.filter(order =>
                toLocalDateString(new Date(order.orderDate)) === dateStr
            );
            const dayRevenue = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
            return {
                date: `${date.getDate()}/${date.getMonth() + 1}`,
                revenue: dayRevenue
            };
        });

        const ordersByDay = dateRange.map(date => {
            const dateStr = toLocalDateString(date);
            const dayOrders = filteredOrders.filter(order =>
                toLocalDateString(new Date(order.orderDate)) === dateStr
            );
            return {
                date: `${date.getDate()}/${date.getMonth() + 1}`,
                orders: dayOrders.length
            };
        });

        return {
            totalRevenue,
            orderCount,
            revenueByDay,
            ordersByDay
        };
    };


    const calculateTrendsData = (orders, trendsType) => {
        const today = new Date();
        let data = { revenueByPeriod: [], ordersByPeriod: [] };

        switch (trendsType) {
            case "daily":
                const startDaily = new Date(today);
                startDaily.setDate(today.getDate() - 6);

                const dailyDates = [];
                const currentDailyDate = new Date(startDaily);
                while (currentDailyDate <= today) {
                    dailyDates.push(new Date(currentDailyDate));
                    currentDailyDate.setDate(currentDailyDate.getDate() + 1);
                }

                data.revenueByPeriod = dailyDates.map(date => {
                    const dateStr = toLocalDateString(date);
                    const dayOrders = orders.filter(order =>
                        toLocalDateString(new Date(order.orderDate)) === dateStr
                    );
                    const dayRevenue = dayOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                    return {
                        date: `${date.getDate()}/${date.getMonth() + 1}`,
                        revenue: dayRevenue
                    };
                });

                data.ordersByPeriod = dailyDates.map(date => {
                    const dateStr = toLocalDateString(date);
                    const dayOrders = orders.filter(order =>
                        toLocalDateString(new Date(order.orderDate)) === dateStr
                    );
                    return {
                        date: `${date.getDate()}/${date.getMonth() + 1}`,
                        orders: dayOrders.length
                    };
                });
                break;

            case "monthly":

                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const monthlyData = Array(12).fill(0).map((_, index) => {
                    const monthDate = new Date(today);
                    monthDate.setMonth(today.getMonth() - (11 - index));

                    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
                    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

                    const monthOrders = orders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= monthStart && orderDate <= monthEnd;
                    });

                    const monthRevenue = monthOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                    return {
                        date: monthNames[monthDate.getMonth()],
                        revenue: monthRevenue,
                        orders: monthOrders.length
                    };
                });

                data.revenueByPeriod = monthlyData.map(item => ({
                    date: item.date,
                    revenue: item.revenue
                }));

                data.ordersByPeriod = monthlyData.map(item => ({
                    date: item.date,
                    orders: item.orders
                }));
                break;

            case "yearly":

                const currentYear = today.getFullYear();
                const yearlyData = Array(5).fill(0).map((_, index) => {
                    const year = currentYear - (4 - index);

                    const yearStart = new Date(year, 0, 1);
                    const yearEnd = new Date(year, 11, 31);

                    const yearOrders = orders.filter(order => {
                        const orderDate = new Date(order.orderDate);
                        return orderDate >= yearStart && orderDate <= yearEnd;
                    });

                    const yearRevenue = yearOrders.reduce((sum, order) => sum + order.finalAmount, 0);
                    return {
                        date: year.toString(),
                        revenue: yearRevenue,
                        orders: yearOrders.length
                    };
                });

                data.revenueByPeriod = yearlyData.map(item => ({
                    date: item.date,
                    revenue: item.revenue
                }));

                data.ordersByPeriod = yearlyData.map(item => ({
                    date: item.date,
                    orders: item.orders
                }));
                break;
        }

        return data;
    };


    const fetchDashboardData = async () => {
        setIsLoading(true);
        setLoading(true);

        try {
            const dateParams = getDateRangeParams();


            const orders = await fetchAllOrders();

            if (orders.length > 0) {

                const calculatedSalesData = calculateSalesData(orders, dateParams);
                setSalesData(calculatedSalesData);


                const calculatedTrendsData = calculateTrendsData(orders, trendsDateRange);
                setTrendsData(calculatedTrendsData);
            }

            setLastUpdated(new Date());
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "Failed to load dashboard data. Please try again.",
                icon: "error",
                confirmButtonColor: "#45a049",
            });
        } finally {
            setIsLoading(false);
            setLoading(false);
        }
    };


    useEffect(() => {
        const styleEl = document.createElement("style");
        styleEl.textContent = `
      .my-swal-container {
        z-index: 20000 !important;
      }
    `;
        document.head.appendChild(styleEl);

        fetchDashboardData();

        return () => {
            document.head.removeChild(styleEl);
        };
    }, []);


    useEffect(() => {
        if (dateRange === "custom" && (!customDateRange.startDate || !customDateRange.endDate)) {
            return;
        }
        fetchDashboardData();
    }, [dateRange, customDateRange, trendsDateRange]);


    const handleRefresh = () => {
        fetchDashboardData();
    };


    const getTrendsRevenueData = () => {
        if (!trendsData || !trendsData.revenueByPeriod) return [];
        return trendsData.revenueByPeriod;
    };

    const getTrendsOrdersData = () => {
        if (!trendsData || !trendsData.ordersByPeriod) return [];
        return trendsData.ordersByPeriod;
    };


    const getPastMonthsOptions = () => {
        const today = new Date();
        const options = [];

        for (let i = 0; i < 12; i++) {
            const monthDate = new Date(today);
            monthDate.setMonth(today.getMonth() - i);

            const monthName = monthDate.toLocaleString('default', { month: 'long' });
            const year = monthDate.getFullYear();

            options.push({
                value: `${monthDate.getMonth()}-${year}`,
                label: `${monthName} ${year}`,
                month: monthDate.getMonth(),
                year: year
            });
        }

        return options;
    };


    const getPastYearsOptions = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const options = [];

        for (let i = 0; i < 5; i++) {
            const year = currentYear - i;
            options.push({
                value: year.toString(),
                label: year.toString(),
                year: year
            });
        }

        return options;
    };

    const indexOfLastOrder = salesListPage * salesListPerPage;
    const indexOfFirstOrder = indexOfLastOrder - salesListPerPage;
    const currentOrders = filteredSalesList.slice(indexOfFirstOrder, indexOfLastOrder);
    const totalSalesListPages = Math.ceil(filteredSalesList.length / salesListPerPage);
    const paginateSalesList = (pageNumber) => setSalesListPage(pageNumber);

    const handlePrint = () => {
        setIsExportDropdownOpen(false);
        const printFrame = document.createElement('iframe');

        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';

        document.body.appendChild(printFrame);


        const reportData = getReportData();


        let viewTypeDescription = "";
        switch (salesListView) {
            case "last30days":
                viewTypeDescription = "Daily (Past 30 Days)";
                break;
            case "monthlyPast12":
                viewTypeDescription = "Monthly (Past 12 Months)";
                break;
            case "yearlyPast5":
                viewTypeDescription = "Yearly (Past 5 Years)";
                break;
            case "yearToDate":
                viewTypeDescription = "Year to Date";
                break;
            case "customRange":
                if (salesListCustomDateRange.startDate && salesListCustomDateRange.endDate) {
                    const startFormatted = formatDateOnly(salesListCustomDateRange.startDate);
                    const endFormatted = formatDateOnly(salesListCustomDateRange.endDate);
                    viewTypeDescription = `Custom Range (${startFormatted} - ${endFormatted})`;
                } else {
                    viewTypeDescription = "Custom Date Range";
                }
                break;
            case "specificMonth":
                if (selectedMonth) {
                    const monthParts = selectedMonth.split('-');
                    const monthIndex = parseInt(monthParts[0]);
                    const year = parseInt(monthParts[1]);
                    const monthDate = new Date(year, monthIndex, 1);
                    viewTypeDescription = `${monthDate.toLocaleString('default', { month: 'long' })} ${year}`;
                } else {
                    viewTypeDescription = "Specific Month";
                }
                break;
            case "specificYear":
                viewTypeDescription = selectedYear ? `Year ${selectedYear}` : "Specific Year";
                break;
            default:
                viewTypeDescription = "Custom Period";
        }


        const tableRowsHTML = reportData.data.map((item, index) => `
            <tr>
                <td>${index + 1}</td>
                <td>${item.displayDate}</td>
                <td>${item.orderCount}</td>
                <td>${formatCurrency(item.totalAmount)}</td>
            </tr>
        `).join('');


        const reportHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Sales Report</title>
                <meta charset="UTF-8">
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: 15px;
                        color: #000;
                        background-color: white;
                        font-size: 12px;
                        line-height: 1.3;
                    }
                    .report-container {
                        max-width: 1000px;
                        margin: 0 auto;
                        padding: 15px;
                        background-color: white;
                    }
                    .header-row {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 20px;
                        border-bottom: 1px solid #000;
                        padding-bottom: 10px;
                    }
                    .company-header {
                        max-width: 60%;
                    }
                    .company-name {
                        font-size: 16px;
                        font-weight: bold;
                        text-transform: uppercase;
                        margin-bottom: 2px;
                    }
                    .company-address {
                        font-size: 12px;
                    }
                    .report-title {
                        font-weight: bold;
                        font-size: 16px;
                        text-align: right;
                    }
                    .report-subtitle {
                        font-size: 14px;
                        margin: 10px 0;
                        font-weight: bold;
                    }
                    .report-info {
                        margin-bottom: 15px;
                    }
                    .info-row {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 8px;
                    }
                    .report-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                    }
                    .report-table th, 
                    .report-table td {
                        padding: 8px;
                        border: 1px solid #ddd;
                        font-size: 11px;
                        text-align: left;
                    }
                    .report-table th {
                        background-color: #f5f5f5;
                        font-weight: bold;
                    }
                    .report-table td:nth-child(3),
                    .report-table td:nth-child(4),
                    .report-table th:nth-child(3),
                    .report-table th:nth-child(4) {
                        text-align: right;
                    }
                    .no-data {
                        text-align: center;
                        padding: 20px;
                        font-style: italic;
                        color: #666;
                    }
                    .summary-section {
                        margin-top: 20px;
                        border-top: 1px solid #ddd;
                        padding-top: 15px;
                    }
                    .summary-row {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 5px;
                    }
                    .summary-label {
                        font-weight: bold;
                    }
                    .page-footer {
                        margin-top: 30px;
                        text-align: center;
                        font-size: 10px;
                        color: #666;
                        border-top: 1px solid #ddd;
                        padding-top: 10px;
                    }
                    .printed-on {
                        font-size: 11px;
                        color: #555;
                        margin-bottom: 6px;
                    }
                    @media print {
                        /* margin:0 suppresses the browser's own print header/footer (its
                           US-format date, the page title and the URL). The page margin is
                           put back on the body so the layout is unchanged. */
                        @page {
                            margin: 0;
                        }
                        body {
                            padding: 12mm 10mm;
                            background-color: white;
                        }
                        .report-container {
                            border: none;
                            padding: 0;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="report-container">
                    <div class="printed-on">${formatDateTime(new Date())}</div>
                    <div class="header-row">
                        <div class="company-header">
                            <div class="company-name">ARDENCY CONSTRUCTION AND TRADING SDN. BHD.</div>
                            <div class="company-address">No.21, Jalan Wawasan 2C/KU7, Sungai Kapar Indah, 42200 Kapar, Selangor</div>
                        </div>
                        
                        <div class="report-title">SALES REPORT</div>
                    </div>
                    
                    <div class="report-info">
                        <div class="report-subtitle">Report Period: ${viewTypeDescription}</div>
                    </div>
                    
                    <table class="report-table">
                        <thead>
                            <tr>
                                <th style="width: 50px;">No.</th>
                                <th>Time Period</th>
                                <th style="width: 120px;">No. of Orders</th>
                                <th style="width: 120px;">Order Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${reportData.data.length > 0 ? tableRowsHTML :
                '<tr><td colspan="4" class="no-data">No sales data found for the selected period.</td></tr>'}
                        </tbody>
                        ${reportData.data.length > 0 ? `
                        <tfoot>
                            <tr>
                                <td colspan="2"><strong>TOTAL</strong></td>
                                <td style="text-align: right;"><strong>${reportData.summary.totalOrders}</strong></td>
                                <td style="text-align: right;"><strong>${formatCurrency(reportData.summary.totalAmount)}</strong></td>
                            </tr>
                        </tfoot>` : ''}
                    </table>
                    
                    ${reportData.data.length > 0 ? `
                    <div class="summary-section">
                        <div class="summary-row">
                            <span class="summary-label">Total Orders:</span>
                            <span>${reportData.summary.totalOrders}</span>
                        </div>
                        <div class="summary-row">
                            <span class="summary-label">Total Revenue:</span>
                            <span>${formatCurrency(reportData.summary.totalAmount)}</span>
                        </div>
                        ${reportData.summary.totalOrders > 0 ? `
                        <div class="summary-row">
                            <span class="summary-label">Average Order Value:</span>
                            <span>${formatCurrency(reportData.summary.totalAmount / reportData.summary.totalOrders)}</span>
                        </div>` : ''}
                    </div>` : ''}
                    
                    <div class="page-footer">
                        End of Report
                    </div>
                </div>
            </body>
            </html>
        `;

        const frameDoc = printFrame.contentWindow || printFrame.contentDocument.document || printFrame.contentDocument;
        frameDoc.document.open();
        frameDoc.document.write(reportHTML);
        frameDoc.document.close();

        printFrame.onload = function () {
            try {
                frameDoc.focus();
                frameDoc.print();

                setTimeout(function () {
                    document.body.removeChild(printFrame);
                }, 1000);
            } catch (e) {

                const originalContent = document.body.innerHTML;
                document.body.innerHTML = reportHTML;
                window.print();
                document.body.innerHTML = originalContent;
            }
        };
    };

    const handleCsvExport = () => {
        setIsExportDropdownOpen(false);
        const reportData = getReportData();

        let viewTypeDescription = "";
        switch (salesListView) {
            case "last30days":
                viewTypeDescription = "Daily (Past 30 Days)";
                break;
            case "monthlyPast12":
                viewTypeDescription = "Monthly (Past 12 Months)";
                break;
            case "yearlyPast5":
                viewTypeDescription = "Yearly (Past 5 Years)";
                break;
            case "yearToDate":
                viewTypeDescription = "Year to Date";
                break;
            case "customRange":
                if (salesListCustomDateRange.startDate && salesListCustomDateRange.endDate) {
                    const startFormatted = formatDateOnly(salesListCustomDateRange.startDate);
                    const endFormatted = formatDateOnly(salesListCustomDateRange.endDate);
                    viewTypeDescription = `Custom Range (${startFormatted} - ${endFormatted})`;
                } else {
                    viewTypeDescription = "Custom Date Range";
                }
                break;
            case "specificMonth":
                if (selectedMonth) {
                    const monthParts = selectedMonth.split('-');
                    const monthIndex = parseInt(monthParts[0]);
                    const year = parseInt(monthParts[1]);
                    const monthDate = new Date(year, monthIndex, 1);
                    viewTypeDescription = `${monthDate.toLocaleString('default', { month: 'long' })} ${year}`;
                } else {
                    viewTypeDescription = "Specific Month";
                }
                break;
            case "specificYear":
                viewTypeDescription = selectedYear ? `Year ${selectedYear}` : "Specific Year";
                break;
            default:
                viewTypeDescription = "Custom Period";
        }

        const escapeCSV = (field) => {
            if (field === null || field === undefined) return '';
            const stringField = String(field);

            if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {

                return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
        };

        const addRow = (...fields) => {
            return fields.map(field => escapeCSV(field)).join(',') + '\n';
        };

        let csvContent = addRow("ARDENCY CONSTRUCTION AND TRADING SDN. BHD.");
        csvContent += addRow("No.21, Jalan Wawasan 2C/KU7, Sungai Kapar Indah, 42200 Kapar, Selangor");
        csvContent += addRow("SALES REPORT");
        csvContent += addRow(`Report Period: ${viewTypeDescription}`);
        csvContent += addRow(`Generated on: ${formatDateTime(new Date())}`);
        csvContent += '\n';
        csvContent += addRow("No.", "Time Period", "No. of Orders", "Order Amount (RM)");

        if (reportData.data.length > 0) {
            reportData.data.forEach((item, index) => {
                csvContent += addRow(
                    (index + 1),
                    item.displayDate,
                    item.orderCount,
                    item.totalAmount.toFixed(2)
                );
            });

            csvContent += '\n';
            csvContent += addRow(
                "TOTAL",
                "",
                reportData.summary.totalOrders,
                reportData.summary.totalAmount.toFixed(2)
            );

            if (reportData.summary.totalOrders > 0) {
                const average = reportData.summary.totalAmount / reportData.summary.totalOrders;
                csvContent += addRow(
                    "Average Order Value (RM)",
                    "",
                    "",
                    average.toFixed(2)
                );
            }
        } else {
            csvContent += addRow("No sales data found for the selected period.");
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const fileName = `Ardency_Sales_Report_${viewTypeDescription.replace(/[^a-zA-Z0-9]/g, '_')}_${toLocalDateString(new Date())}.csv`;
        const link = document.createElement('a');

        if (window.navigator.msSaveOrOpenBlob) {

            window.navigator.msSaveBlob(blob, fileName);
        } else {

            const url = URL.createObjectURL(blob);
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            setTimeout(function () {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        }


        setIsExportDropdownOpen(false);
    };

    if (loading) {
        return (
            <div className="admin-sales-dashboard-container">
                <div className="admin-sales-dashboard-loading">
                    <Loading />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-sales-dashboard-container">
            {/* Header Section */}
            <div className="admin-sales-dashboard-header">
                <button
                    className="admin-sales-dashboard-back-btn"
                    onClick={() => navigate("/admin/dashboard")}
                >
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </button>
            </div>

            {/* Title Section */}
            <div className="admin-sales-dashboard-title">
                <h1>Sales Analytics</h1>
            </div>
 
            <div className="admin-sales-dashboard-refresh" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                    className="admin-sales-dashboard-refresh-btn"
                    onClick={handleRefresh}
                >
                    <i className="fas fa-sync-alt"></i> Search
                </button> 
            </div>

            {/* Layout Row for Overview and Reports */}
            <div className="admin-sales-dashboard-row">
                {/* Sales Overview Section */}
                <div className="admin-sales-dashboard-overview">
                    <div className="admin-sales-dashboard-print-section">
                        <div className="admin-sales-dashboard-trends-header">
                            <h2>Sales Overview</h2>
                        </div>

                        <div className="admin-sales-dashboard-controls">
                            <div className="admin-sales-dashboard-control-group">
                                <label>Date Range</label>
                                <select
                                    value={dateRange}
                                    onChange={(e) => setDateRange(e.target.value)}
                                >
                                    <option value="today">Today</option>
                                    <option value="yesterday">Yesterday</option>
                                    <option value="last7days">Last 7 Days</option>
                                    <option value="last30days">Last 30 Days</option>
                                    <option value="monthToDate">Month to Date</option>
                                    <option value="quarterToDate">Quarter to Date</option>
                                    <option value="yearToDate">Year to Date</option>
                                    <option value="1year">1 Year</option>
                                    <option value="custom">Custom Range</option>
                                </select>
                            </div>

                            {dateRange === "custom" && (
                                <div className="admin-sales-dashboard-custom-date">
                                    <div className="admin-sales-dashboard-control-group">
                                        <label>Start Date</label>
                                        <input
                                            type="date"
                                            value={customDateRange.startDate || ""}
                                            onChange={(e) =>
                                                setCustomDateRange({
                                                    ...customDateRange,
                                                    startDate: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                    <div className="admin-sales-dashboard-control-group">
                                        <label>End Date</label>
                                        <input
                                            type="date"
                                            value={customDateRange.endDate || ""}
                                            onChange={(e) =>
                                                setCustomDateRange({
                                                    ...customDateRange,
                                                    endDate: e.target.value,
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            )}

                            {/* KPIs Section (moved inside controls) */}
                            <div className="admin-sales-dashboard-kpis" style={{ marginTop: '20px', width: '100%' }}>
                                {/* Total Revenue KPI */}
                                <div className="admin-sales-dashboard-kpi-card">
                                    <div className="admin-sales-dashboard-kpi-title">
                                        Total Revenue
                                    </div>
                                    <div className="admin-sales-dashboard-kpi-value">
                                        {formatCurrency(salesData.totalRevenue || 0)}
                                    </div>
                                </div>

                                {/* Number of Orders KPI */}
                                <div className="admin-sales-dashboard-kpi-card">
                                    <div className="admin-sales-dashboard-kpi-title">
                                        Number of Orders
                                    </div>
                                    <div className="admin-sales-dashboard-kpi-value">
                                        {salesData.orderCount || 0}
                                    </div>
                                </div>

                                {/* Average Order Value KPI */}
                                <div className="admin-sales-dashboard-kpi-card">
                                    <div className="admin-sales-dashboard-kpi-title">
                                        Average Order Value
                                    </div>
                                    <div className="admin-sales-dashboard-kpi-value">
                                        {formatCurrency(
                                            salesData.orderCount
                                                ? salesData.totalRevenue / salesData.orderCount
                                                : 0
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Row container for Sales Trends and Sales List */}
            <div className="admin-sales-dashboard-row">
                {/* Sales Trends Section */}
                <div className="admin-sales-dashboard-trends-container" style={{ flex: 5 }}>
                    <div className="admin-sales-dashboard-print-section">
                        <div className="admin-sales-dashboard-trends-header">
                            <h2>Sales Trends</h2>
                            <div className="admin-sales-dashboard-trends-controls">
                                <div className="admin-sales-dashboard-control-group">
                                    <label>Time Period</label>
                                    <select
                                        value={trendsDateRange}
                                        onChange={(e) => setTrendsDateRange(e.target.value)}
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="yearly">Yearly</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        {/* Revenue Over Time Chart */}
                        <div className="admin-sales-dashboard-chart-card">
                            <div className="admin-sales-dashboard-chart-title">
                                Revenue Over Time
                            </div>
                            <div className="admin-sales-dashboard-chart">
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart
                                        data={getTrendsRevenueData()}
                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        style={{ fontFamily: 'Arial, sans-serif' }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="revenue"
                                            name="Revenue"
                                            stroke="#2486d2"
                                            strokeWidth={2}
                                            activeDot={{ r: 8 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        {/* Orders Over Time Chart */}
                        <div className="admin-sales-dashboard-chart-card">
                            <div className="admin-sales-dashboard-chart-title">
                                Orders Over Time
                            </div>
                            <div className="admin-sales-dashboard-chart">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart
                                        data={getTrendsOrdersData()}
                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        style={{ fontFamily: 'Arial, sans-serif' }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis
                                            tickCount={10}
                                            allowDecimals={false}
                                            domain={[0, 'dataMax']}
                                        />
                                        <Tooltip />
                                        <Legend />
                                        <Bar
                                            dataKey="orders"
                                            name="Orders"
                                            fill="#2486d2"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>

                {/* New Sales List Section */}
                <div className="admin-sales-dashboard-trends-container" style={{ flex: 5 }}>
                    <div className="admin-sales-dashboard-print-section">
                        <div className="admin-sales-dashboard-trends-header">
                            <h2>Sales List</h2>
                            <div className="admin-sales-dashboard-trends-controls">
                                {salesListView === "specificMonth" && (
                                    <div className="admin-sales-dashboard-control-group">
                                        <label>Select Month</label>
                                        <select
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                            className="admin-sales-dashboard-month-select"
                                        >
                                            <option value="">Select a month</option>
                                            {getPastMonthsOptions().map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {salesListView === "specificYear" && (
                                    <div className="admin-sales-dashboard-control-group">
                                        <label>Select Year</label>
                                        <select
                                            value={selectedYear}
                                            onChange={(e) => setSelectedYear(e.target.value)}
                                            className="admin-sales-dashboard-year-select"
                                        >
                                            <option value="">Select a year</option>
                                            {getPastYearsOptions().map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div className="admin-sales-dashboard-control-group">
                                    <label>Report Type</label>
                                    <select
                                        value={salesListView}
                                        onChange={(e) => {
                                            setSalesListView(e.target.value);
                                            setSelectedMonth("");
                                            setSelectedYear("");
                                        }}
                                    >
                                        <option value="last30days">Daily (Past 30 Days)</option>
                                        <option value="monthlyPast12">Monthly (Past 12 Months)</option>
                                        <option value="yearlyPast5">Yearly (Past 5 Years)</option>
                                        <option value="yearToDate">Year to Date</option>
                                        <option value="customRange">Custom Range</option>
                                        <option disabled>── Specific Month/Year</option>
                                        <option value="specificMonth">Month</option>
                                        <option value="specificYear">Year</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="admin-sales-dashboard-custom-date">
                            {salesListView === "customRange" && (
                                <div className="admin-sales-dashboard-control-group">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        value={salesListCustomDateRange.startDate || ""}
                                        onChange={(e) =>
                                            setSalesListCustomDateRange({
                                                ...salesListCustomDateRange,
                                                startDate: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            )}
                            {salesListView === "customRange" && (
                                <div className="admin-sales-dashboard-control-group">
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        value={salesListCustomDateRange.endDate || ""}
                                        onChange={(e) =>
                                            setSalesListCustomDateRange({
                                                ...salesListCustomDateRange,
                                                endDate: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            )}
                            <div className="export-dropdown-container">
                                <button
                                    className="admin-sales-dashboard-print-btn"
                                    onClick={toggleExportDropdown}
                                >
                                    <i className="fas fa-file-export"></i> Export Report
                                </button>

                                {isExportDropdownOpen && (
                                    <div className="export-dropdown-menu">
                                        <button
                                            className="export-dropdown-item"
                                            onClick={handlePrint}
                                        >
                                            <i className="fas fa-file-pdf"></i> Export as PDF
                                        </button>
                                        <button
                                            className="export-dropdown-item"
                                            onClick={handleCsvExport}>
                                            <i className="fas fa-file-csv"></i> Export as CSV
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="admin-sales-table-container">
                            {currentOrders.length > 0 ? (
                                <table className="admin-sales-table">
                                    <thead>
                                        <tr>
                                            <th>Time Period</th>
                                            <th>No. of Orders</th>
                                            <th>Order Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentOrders.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.displayDate}</td>
                                                <td>{item.orderCount}</td>
                                                <td>{formatCurrency(item.totalAmount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td><strong>Total</strong></td>
                                            <td><strong>{salesListSummary.totalOrders}</strong></td>
                                            <td><strong>{formatCurrency(salesListSummary.totalAmount)}</strong></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            ) : (
                                <div className="admin-sales-empty">
                                    <i className="fas fa-shopping-cart"></i>
                                    <p className="primary-message">
                                        No orders found for the selected period.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {filteredSalesList.length > salesListPerPage && (
                            <div className="admin-inquiry-pagination">
                                <button
                                    className="admin-inquiry-pagination-btn"
                                    onClick={() => paginateSalesList(1)}
                                    disabled={salesListPage === 1}
                                >
                                    <i className="fas fa-angle-double-left"></i>
                                </button>
                                <button
                                    className="admin-inquiry-pagination-btn"
                                    onClick={() => paginateSalesList(salesListPage - 1)}
                                    disabled={salesListPage === 1}
                                >
                                    <i className="fas fa-angle-left"></i>
                                </button>

                                {[...Array(totalSalesListPages).keys()].map((number) => {
                                    if (
                                        number + 1 === 1 ||
                                        number + 1 === totalSalesListPages ||
                                        (number + 1 >= salesListPage - 1 && number + 1 <= salesListPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={number + 1}
                                                className={`admin-inquiry-pagination-btn ${salesListPage === number + 1 ? "active" : ""}`}
                                                onClick={() => paginateSalesList(number + 1)}
                                            >
                                                {number + 1}
                                            </button>
                                        );
                                    }

                                    if (
                                        (number + 1 === salesListPage - 2 && salesListPage > 3) ||
                                        (number + 1 === salesListPage + 2 && salesListPage < totalSalesListPages - 2)
                                    ) {
                                        return (
                                            <span key={number + 1} className="admin-inquiry-pagination-btn">
                                                ...
                                            </span>
                                        );
                                    }
                                    return null;
                                })}

                                <button
                                    className="admin-inquiry-pagination-btn"
                                    onClick={() => paginateSalesList(salesListPage + 1)}
                                    disabled={salesListPage === totalSalesListPages}
                                >
                                    <i className="fas fa-angle-right"></i>
                                </button>
                                <button
                                    className="admin-inquiry-pagination-btn"
                                    onClick={() => paginateSalesList(totalSalesListPages)}
                                    disabled={salesListPage === totalSalesListPages}
                                >
                                    <i className="fas fa-angle-double-right"></i>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Per-product sales report (daily / monthly) */}
            <AdminProductSalesReport />
        </div>
    );
}

export default AdminSalesAnalytics;